/* @flow */
import { getterFromKeyPath } from 'trustar/utils';
import { createSelector } from 'reselect';
import { OrderedMap, Map, List } from 'immutable';
import { alphabetize } from 'trustar/utils';
import { map } from 'ramda';
import isString from 'lodash.isstring';

import { enclavesMap, queriedEnclavesMap } from 'modules/enclave-management/getters.js';
import { sharingGroupToFormOptions } from 'modules/groups/utils.js';
import { sectors } from 'modules/sectors/getters.js';
import { Company as CompanyRecord, typeof Quota as QuotaShape, Quota } from './types';
import { getQuotaFromCompany } from './index.js';
import { determineAccessType } from 'modules/enclave/utils';

import { accessTypes } from 'modules/miscellaneous/getters.js';

export const companies = getterFromKeyPath(['companies', 'companies']);

export const loading = getterFromKeyPath(['companies', 'loading']);

export const displayOptions = getterFromKeyPath(['companies', 'options']);

export const quotas = getterFromKeyPath(['companies', 'quotas']);

export const fullyLoaded = getterFromKeyPath(['companies', 'fullyLoaded']);


/**
 * NOTE: In this case the "quota" is actually the list of quotas for the company
 * with this id
 */

export const companiesByGuid = createSelector(
  companies,
  (companies: OrderedMap<any, any>): Map<any, any> =>
    Map(companies.toList().map(x => [x.guid, x]))
);

export const company = (id: string) => createSelector(
  companies,
  (companies: OrderedMap<any, any>): Map<any, any> => companies.get(id),
);

export const companyByGuid = (guid: string) => createSelector(
  companiesByGuid,
  companies => companies.get(guid)
);

export const quota = (guid: string) => createSelector(
  companyByGuid(guid),
  getQuotaFromCompany
);

/**
 * Returns companies filtered by options.filterText
 */
export const filteredCompanies = createSelector(
  [ companies, displayOptions ],
  (companies: OrderedMap<any, any>, options: Map<any, any>) => {
    const enclaveId = options.get('enclaveId');
    return companies
      .toSeq()
      .valueSeq()
      .filter(company => {
        if (!enclaveId) return true;

        if (enclaveId && !company.get('enclaves', Map()).keySeq().contains(enclaveId)) {
          return false;
        }

        // Test all top-level string values
        return company
          .toSeq()
          .valueSeq()
          .filter(isString);
      });
  },
);

// Sector options for save company form
export const sectorOptions = createSelector(
  sectors,
  (sectors: Map<any, any>) =>
    sectors
      .toList()
      .map(({ id, label }) => {
        return {
          value: id,
          label: label,
        };
      })
      .toJS()
);

// whether a given enclave can be assigned to a given company
const canAssignToCompany = (companyGuid: ?string, enclave) => {
  switch(enclave.accessManagementType) {
  case 'ALLOWED':
    return true;
  case 'FORBIDDEN':
    return false;
  case 'COMPANY':
    const companyId = enclave.companyId;
    return companyId != null && companyId === companyGuid;
  }
};

// All enclaves for which company permissions can be manually managed
export const managedEnclaves = (companyGuid: ?string) => createSelector(
  [ enclavesMap ],
  (enclaves: Map<any, any>) => enclaves.filter(x => canAssignToCompany(companyGuid, x))
);

/*
 It's a bit redundant to have two managed enclaves, but we are seperating the queried enclaves
  from the more global ones because of how many different components really on the enclaves above, we
  don't want to accidentally filter out an enclave and have that filter persist elsewhere
*/
export const managedEnclavesWithQuery = (companyGuid: ?string) => createSelector(
  [ queriedEnclavesMap ],
  (enclaves: Map<any, any>) => enclaves.filter(x => canAssignToCompany(companyGuid, x))
);

// construct the object to be used in the enclave selector for the companies form
const createEnclaveOption = (enclave) => {
  const value = enclave.get('id');
  const option = Map({
    value,
    id: value,
  }).merge(enclave);
  return option.set('label', enclave.get('name'));
};

// the list of all enclaves that can be assigned to the company with the specified GUID
// the enclaves will be transformed to the format required for the selector
const enclaveOptionsMap = (guid: string) => createSelector(
  [ managedEnclaves(guid), accessTypes ],
  (enclaves: Map<any, any>, accessTypes) =>
    enclaves
      .map(enclave => createEnclaveOption(enclave))
      .map(e => e.update('maximumAccess', t => accessTypes.get(t)))
);

// Enclave options for save company form
export const enclaveOptions = (guid: string) => createSelector(
  enclaveOptionsMap(guid),
  (enclaveOptionsMap: Map<any, any>) =>
    enclaveOptionsMap
      .toList()
      .sort(alphabetize)
      .toJS()
);
// Enclave options for save company form
export const queriedEnclaveOptions = (guid: string) => createSelector(
  [managedEnclavesWithQuery(guid), accessTypes],
  (enclaveOptionsMap: Map<any, any>, accessTypes) =>
    // Add company access type to enclave and format the data to work with our selector
    enclaveOptionsMap
      .map(enclave => createEnclaveOption(enclave))
      .map(e => e.update('maximumAccess', t => accessTypes.get(t)))
      .toList()
      .sort(alphabetize)
      .toJS()
);

// merges information from the enclave permission object for the company
// with information from the enclave management store about the enclave
const mergeEnclavePermission = (enclavePermission, enclaveOptionsMap) => {
  const enclave = enclaveOptionsMap.get(enclavePermission.get('id'));
  if (!enclave) return null;
  return createEnclaveOption(enclave)
    .set('maximumAccess', determineAccessType(enclavePermission));
};

/* Generate the initial values for the save company form
 * NOTE: FlowFixMes - both sector and company should exist.
 **/
export const getInitialFormValues = (guid: string) => createSelector(
  enclaveOptionsMap(guid),
  sectors,
  filteredCompanies,
  quota(guid),
  (enclaveOptionsMap: Map<any, any>, sectors: Map<any, any>, companies: Map<any, any>, quota: QuotaShape) => {
    if (!guid) return CompanyRecord({ maxUserCount: 25 });
    // $FlowFixMe
    return companies
      .find(c => c.get('guid') === guid)
      .update('enclaves', enclaves =>
        enclaves
          .map(enclavePermission => mergeEnclavePermission(enclavePermission, enclaveOptionsMap))
          .filter(x => x != null)
          .toList()
          .sort(alphabetize))
      .update('sharingGroups', map(sharingGroupToFormOptions))
      .update('rateLimit', x => quota ? quota.get('maxRequests') : undefined)
      .update('requestQuotas', quotas => quotas || List())
      .update('sector', sector => sector.set('value', sector.get('id')));
  }
);

/**
 * Generate initial form values for creating new company. This should
 * add all the open source enclaves that exist at the time of creation.
 *
 * NOTE: The default rate limit for all companies. This simply updates the
 * default value in the form, so the admin can manually set this to whatever is
 * appropriate for that company.
 */
export const getInitialFormValuesForAdd = createSelector(
  [ managedEnclaves(null), accessTypes ],
  (enclaves: Map<any, any>, accessTypes) => {
    return Map({
      enclaves: List(),
      sharingGroups: List(),
      requestQuotas: List([Quota()]),
      rateLimit: 1500, // See NOTE
      maxIngestEmailsCount: 1, // See NOTE
      maxUserCount: 25, // See NOTE
    });
  }
);



// WEBPACK FOOTER //
// ./src/app/modules/companies/getters.js